import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Reveal, { SectionHead } from "./Reveal";
import { submitAudit } from "@/lib/api";

const ATTENDANCE = ["Under 50", "50–200", "150–500", "300–1,000", "1,000+"];
const PLATFORMS = ["Facebook", "YouTube", "Instagram", "TikTok", "Website"];

const EMPTY = {
  church_name: "", leader_name: "", email: "", phone: "", city_state: "",
  attendance: "50–200", platforms: [], challenge: "", vision: "",
};

export default function AuditForm() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const togglePlatform = (p) => setForm((f) => ({
    ...f, platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p],
  }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitAudit(form);
      setDone(true);
      toast.success("Audit application received. We’ll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="audit" data-testid="audit-section" className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <SectionHead eyebrow="Step One" title="Book Your Digital Ministry Visibility Audit."
          sub="Before a new family visits your church, they will likely visit your digital presence first. What are they seeing? Apply below — complimentary for qualified churches." />

        <Reveal delay={0.15}>
          <div className="mt-12 rounded-xl border border-gold/30 bg-navy-surface/70 backdrop-blur-xl p-8 sm:p-12 shadow-[0_0_60px_rgba(212,175,55,0.08)]">
            {done ? (
              <div className="py-12 text-center" data-testid="audit-form-success">
                <span className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-gold text-navy mb-6">
                  <Check size={30} />
                </span>
                <p className="font-display text-2xl uppercase tracking-tight text-white">Application received.</p>
                <p className="mt-4 text-sm sm:text-base text-slate-300 max-w-md mx-auto leading-relaxed">
                  Your church was never called to be invisible. We’ll review your digital presence and reach out
                  within 48 hours to schedule your 30-minute audit call.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="audit-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <input data-testid="audit-church-name-input" className="field" placeholder="Church name *" required
                    value={form.church_name} onChange={set("church_name")} />
                  <input data-testid="audit-leader-name-input" className="field" placeholder="Pastor / leader name *" required
                    value={form.leader_name} onChange={set("leader_name")} />
                  <input data-testid="audit-email-input" className="field" type="email" placeholder="Email address *" required
                    value={form.email} onChange={set("email")} />
                  <input data-testid="audit-phone-input" className="field" type="tel" placeholder="Mobile number *" required
                    value={form.phone} onChange={set("phone")} />
                  <input data-testid="audit-city-state-input" className="field" placeholder="City and state *" required
                    value={form.city_state} onChange={set("city_state")} />
                  <select data-testid="audit-attendance-select" className="field" value={form.attendance} onChange={set("attendance")}>
                    {ATTENDANCE.map((a) => <option key={a} value={a}>Weekly attendance: {a}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-[11px] tracking-[0.22em] uppercase text-slate-400 mb-3">
                    What does your church currently have?
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {PLATFORMS.map((p) => (
                      <button type="button" key={p} data-testid={`audit-platform-${p.toLowerCase()}-toggle`}
                        onClick={() => togglePlatform(p)}
                        className={`rounded-full border px-5 py-2.5 text-xs font-semibold tracking-wide transition-all ${form.platforms.includes(p)
                          ? "border-gold bg-gold text-navy" : "border-white/20 text-slate-300 hover:border-gold/60 hover:text-gold"}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea data-testid="audit-challenge-textarea" className="field min-h-[110px] resize-y" required
                  placeholder="What is your greatest current challenge? *"
                  value={form.challenge} onChange={set("challenge")} />
                <textarea data-testid="audit-vision-textarea" className="field min-h-[110px] resize-y"
                  placeholder="What would becoming more visible online make possible for your ministry?"
                  value={form.vision} onChange={set("vision")} />
                <button data-testid="audit-form-submit-button" disabled={busy}
                  className="w-full rounded-full bg-gold px-8 py-5 text-xs font-bold tracking-[0.2em] uppercase text-navy transition-all hover:bg-gold-hover hover:shadow-[0_0_35px_rgba(212,175,55,0.45)] hover:scale-[1.01] disabled:opacity-60 flex items-center justify-center gap-2">
                  {busy && <Loader2 size={16} className="animate-spin" />}
                  Book My Digital Ministry Audit
                </button>
                <p className="text-[11px] text-slate-500 text-center">
                  Limited Founding Church Partner openings remain this month.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
